import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

/* eslint @nrwl/nx/enforce-module-boundaries: 0 */
import { MatMenu } from '@angular/material/menu';
import { NavigationEnd, Router } from '@angular/router';
import { DataAccessFairsFacade, FairsConfiguration } from '@planone/bau365/data-access-fair-data';
import { BusinessCardImage } from '@planone/bau365/util-user-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { ImageFileTypes } from '@planone/shared/util-pipelines';
import { combineLatest, filter, map, Observable, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'bau365-shell-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnDestroy {
  @Input() isLoggedIn = false;
  @Input() selfServiceLink?: string;
  @Output() burgerMenuToggle = new EventEmitter<boolean>();
  @Output() logout: EventEmitter<void> = new EventEmitter<void>();

  @Input() givenName = '';
  @Input() familyName = '';
  @Input() initials = '';
  @Input() email = '';
  @Input() loginLink = '';
  @Input() profileImage: BusinessCardImage | undefined | null;

  @ViewChild('menu') menu?: MatMenu;

  private unsubscribe: Subject<void> = new Subject<void>();

  fairId$: Observable<string | null>;
  fairsConfiguration$: Observable<FairsConfiguration>;
  isPastFairActive$: Observable<boolean>;

  searchEnabled$: Observable<boolean | undefined>;
  programEnabled$: Observable<boolean | undefined>;
  watchlistEnabled$: Observable<boolean | undefined>;
  hallplanEnabled$: Observable<boolean | undefined>;
  qrCodeScannerEnabled$: Observable<boolean | undefined>;

  routerSubscription: Subscription;
  detailed = true;
  rememberedSize?: string;

  constructor(
    public togglesFacade: DataAccessTogglesFacade,
    private fairsFacade: DataAccessFairsFacade,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.searchEnabled$ = this.togglesFacade.flag('kill-search').pipe(map((searchDisabled) => !searchDisabled));
    this.programEnabled$ = this.togglesFacade.flag('kill-program').pipe(map((programDisabled) => !programDisabled));
    this.hallplanEnabled$ = this.togglesFacade
      .flag('kill-hall-plan')
      .pipe(map((hallplanDisabled) => !hallplanDisabled));
    this.watchlistEnabled$ = this.togglesFacade
      .flag('kill-watchlist')
      .pipe(map((watchlistDisabled) => !watchlistDisabled));
    this.qrCodeScannerEnabled$ = this.togglesFacade
      .flag('kill-qrcode-scanner')
      .pipe(map((qrCodeScannerDisabled) => !qrCodeScannerDisabled));

    this.fairsConfiguration$ = this.fairsFacade.fairsConfig$;
    this.fairId$ = this.fairsFacade.fairIdFromRoute$;
    this.isPastFairActive$ = combineLatest([this.fairId$, this.fairsConfiguration$]).pipe(
      map(([fairId, fairsConfiguration]) => {
        if (!fairId) return false;
        return ![fairsConfiguration.activeFair, fairsConfiguration.bauInsights].includes(fairId);
      })
    );
    this.routerSubscription = router.events
      .pipe(
        takeUntil(this.unsubscribe),
        filter((change) => change instanceof NavigationEnd),
        map((change) => <NavigationEnd>change)
      )
      .subscribe((change) => {
        const { url } = change;
        this.detailed = !(url.startsWith('/profilecompletion') && url.includes('mode=initial'));
        this.changeDetectorRef.detectChanges();
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const target: Window = <Window>event.target;

    if (target.innerWidth < 1395 && this.rememberedSize !== 'small') {
      this.rememberedSize = 'small';

      this.menu?.closed.next();
    } else if (target.innerWidth >= 1395 && this.rememberedSize !== 'big') {
      this.rememberedSize = 'big';
    }
  }

  isAllFairsRouteActive(route: string[], fragment?: string): Observable<boolean> {
    return combineLatest([this.fairId$, this.fairsConfiguration$]).pipe(
      map(([fairId, fairsConfiguration]) => {
        const excludedFromCheck = [fairsConfiguration.activeFair, fairsConfiguration.bauInsights];
        if (fairId && excludedFromCheck.includes(fairId)) {
          return false;
        }
        const urlTree = this.router.createUrlTree(route, { fragment });
        return this.router.isActive(urlTree, {
          matrixParams: 'ignored',
          queryParams: 'ignored',
          paths: 'subset',
          fragment: fragment ? 'exact' : 'ignored',
        });
      })
    );
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  openOrCloseMenu(): void {
    this.burgerMenuToggle.emit(true);
  }

  closeMenu(): void {
    this.burgerMenuToggle.emit(false);
  }

  doLogout() {
    this.logout.next();
  }

  protected readonly ImageFileTypes = ImageFileTypes;
}
